import {
  AddRoomsToSpeakingSessionRequest,
  AvailableSpeakingSessionDateDto,
  CreateSpeakingSessionDto,
  IGetSpeakingSessionsFromSpeakingSessionServiceQuery,
  LinkedLrwSessionDto,
  PagedResultOfSpeakingSessionDto,
  RoomEventDto,
  RoomForSpeakingSessionDto,
  SpeakingSessionDetailsDto,
  SpeakingSessionRoomDto,
  UpdateSpeakingSessionRequest,
  VenueForSpeakingSessionDto,
  speakingSessionClient,
} from 'api/planning';
import { api } from '../api';
import { getSortingAndPaginationQuery, invalidateOn } from 'utils';
import { SpeakingSessionTableState } from './types';
import {
  SPEAKING_SESSION_ROOMS_LIST_QUERY_TAG,
  SPEAKING_SESSION_DETAILS_QUERY_TAG,
  SPEAKING_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
  SPEAKING_SESSION_ROOMS_EVENTS_LIST_QUERY_TAG,
} from 'store';

export const speakingSessionApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAvailableVenuesForSpeakingSession: builder.query<
      VenueForSpeakingSessionDto[],
      { sessionDate: string; locationId: number | undefined }
    >({
      query: ({ sessionDate, locationId: _ }) =>
        speakingSessionClient.getAvailableVenues(sessionDate),
    }),
    getAvailableDatesForSpeakingSession: builder.query<
      AvailableSpeakingSessionDateDto[],
      { locationId: number }
    >({
      query: ({ locationId: _ }) => speakingSessionClient.getAvailableDates(),
    }),
    createSpeakingSession: builder.mutation<number, CreateSpeakingSessionDto>({
      query: (request) => speakingSessionClient.createSpeakingSession(request),
    }),
    getSpeakingSessions: builder.query<
      PagedResultOfSpeakingSessionDto,
      { tableState: SpeakingSessionTableState; locationId: number | undefined }
    >({
      query: ({ tableState, locationId: _ }) => {
        const query: IGetSpeakingSessionsFromSpeakingSessionServiceQuery =
          getSortingAndPaginationQuery(tableState);

        return speakingSessionClient.getSpeakingSessions(query);
      },
    }),
    getSpeakingSessionDetails: builder.query<
      SpeakingSessionDetailsDto,
      { speakingSessionId: number; locationId: number | undefined }
    >({
      query: ({ speakingSessionId, locationId: _ }) =>
        speakingSessionClient.getSpeakingSessionDetails(speakingSessionId, {
          customErrorHandling: (response) => response.status === 404,
        }),
      providesTags: () => [{ type: SPEAKING_SESSION_DETAILS_QUERY_TAG }],
    }),
    getLinkedLrwSessions: builder.query<LinkedLrwSessionDto[], { speakingSessionId: number }>({
      query: ({ speakingSessionId }) => speakingSessionClient.getLinkedSessions(speakingSessionId),
    }),
    getAvailableRooms: builder.query<RoomForSpeakingSessionDto[], { speakingSessionId: number }>({
      query: ({ speakingSessionId }) => speakingSessionClient.getAvailableRooms(speakingSessionId),
    }),
    getRoomsEvents: builder.query<RoomEventDto[], { speakingSessionId: number }>({
      query: ({ speakingSessionId }) =>
        speakingSessionClient.getSpeakingSessionRoomEvents(speakingSessionId),
      providesTags: () => [{ type: SPEAKING_SESSION_ROOMS_EVENTS_LIST_QUERY_TAG }],
    }),
    assignRoomsToSpeakingSession: builder.mutation<
      void,
      { speakingSessionId: number; request: AddRoomsToSpeakingSessionRequest }
    >({
      query: ({ speakingSessionId, request }) =>
        speakingSessionClient.assignRooms(speakingSessionId, request),
      invalidatesTags: invalidateOn({
        success: [
          SPEAKING_SESSION_ROOMS_LIST_QUERY_TAG,
          SPEAKING_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
          SPEAKING_SESSION_ROOMS_EVENTS_LIST_QUERY_TAG,
        ],
      }),
    }),
    getSpeakingSessionRooms: builder.query<SpeakingSessionRoomDto[], { speakingSessionId: number }>(
      {
        query: ({ speakingSessionId }) => speakingSessionClient.getRooms(speakingSessionId),
        providesTags: () => [{ type: SPEAKING_SESSION_ROOMS_LIST_QUERY_TAG }],
      }
    ),
    releaseSpeakingSession: builder.mutation<void, { speakingSessionId: number }>({
      query: ({ speakingSessionId }) => speakingSessionClient.releaseSession(speakingSessionId),
      invalidatesTags: invalidateOn({
        success: [SPEAKING_SESSION_DETAILS_QUERY_TAG, SPEAKING_SESSION_ALLOWED_ACTIONS_QUERY_TAG],
      }),
    }),
    updateSpeakingSession: builder.mutation<
      void,
      { speakingSessionId: number; request: UpdateSpeakingSessionRequest }
    >({
      query: ({ speakingSessionId, request }) =>
        speakingSessionClient.updateSpeakingSession(speakingSessionId, request),
      invalidatesTags: invalidateOn({
        success: [SPEAKING_SESSION_DETAILS_QUERY_TAG, SPEAKING_SESSION_ALLOWED_ACTIONS_QUERY_TAG],
      }),
    }),
    removeSpeakingSession: builder.mutation<void, { speakingSessionId: number }>({
      query: ({ speakingSessionId }) => speakingSessionClient.removeSession(speakingSessionId),
    }),
    removeSpeakingSessionRoom: builder.mutation<
      void,
      { speakingSessionId: number; speakingSessionRoomId: number }
    >({
      query: ({ speakingSessionId, speakingSessionRoomId }) =>
        speakingSessionClient.removeSpeakingSessionRoom(speakingSessionId, speakingSessionRoomId),
      invalidatesTags: invalidateOn({
        success: [
          SPEAKING_SESSION_ROOMS_LIST_QUERY_TAG,
          SPEAKING_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
          SPEAKING_SESSION_ROOMS_EVENTS_LIST_QUERY_TAG,
        ],
      }),
    }),
  }),
});

export const {
  useGetAvailableVenuesForSpeakingSessionQuery,
  useGetAvailableDatesForSpeakingSessionQuery,
  useCreateSpeakingSessionMutation,
  useGetSpeakingSessionsQuery,
  useGetSpeakingSessionDetailsQuery,
  useGetAvailableRoomsQuery,
  useGetRoomsEventsQuery,
  useAssignRoomsToSpeakingSessionMutation,
  useGetSpeakingSessionRoomsQuery,
  useReleaseSpeakingSessionMutation,
  useUpdateSpeakingSessionMutation,
  useRemoveSpeakingSessionMutation,
  useRemoveSpeakingSessionRoomMutation,
  useGetLinkedLrwSessionsQuery,
} = speakingSessionApi;
